import React from 'react';
import {ForeignBookModel} from "../../../models/foreign-book.model";
import {Grid} from "@mui/material";
import styled from "styled-components";
import {Chip, Hidden, Tab, Tabs} from "@material-ui/core";
import {Colors} from "../../../theme/colors";
import {CustomTabPanel} from "../../ui-base/tab-panel";
import {CustomFileIcon} from "../../ui-base/icons/custom-file-icon";
import {StyledButton} from "../../ui-base/button";
import {PublisherService} from "../../../services/publisher.service";
import {useTranslation} from "react-i18next";
import {BaseService} from "../../../services/base.service";
import {BookCover} from "../../shared/book-cover";

export const ForeignBookList: React.FC<{ book: ForeignBookModel }> = ({book}) => {
    const [tabActive, setTabActive] = React.useState(0);
    const {t} = useTranslation(['publisher', 'common']);

    const bookDetailsClicked = () => {
        PublisherService.foreignBookLinkClicked(book.id);
    }

    const publisherDetailsClicked = () => {
        PublisherService.foreignPublisherLinkClicked(book.id);
    }

    return (
        <PublisherBookContainer item xs={12} container direction={"row"}>
            <Hidden mdDown>
                <Grid item style={{position: 'relative', flex: 0, minWidth: '230px'}}>
                    {book.book_cover?.file &&
                        <StyledImageContainer item>
                            <StyledImg src={book.book_cover.file}/>
                        </StyledImageContainer>
                    }

                    {!book.book_cover && !book.book_cover?.file &&
                        <StyledImageContainer item>
                            <BookCover
                                title={book?.title}
                                description={book?.description}
                                author={book.authors}
                            />
                        </StyledImageContainer>
                    }

                    <Grid item container direction={"column"} spacing={1}
                          style={{width: '100%', marginLeft: 0, paddingLeft: '10px', paddingBottom: '10px'}}>
                        <Grid item>
                            <StyledSpan1>{t('foreignBook.releaseCountry')}:</StyledSpan1>
                            <StyledSpan2>{book.release_country}</StyledSpan2>
                        </Grid>

                        <Grid item>
                            <StyledSpan1>{t('foreignBook.bookLanguage')}:</StyledSpan1>
                            <StyledSpan2>{book.book_language}</StyledSpan2>
                        </Grid>

                        <Grid item>
                            <StyledSpan1>{t('foreignBook.translations')}:</StyledSpan1>
                            <StyledSpan2>{book.translations}</StyledSpan2>
                        </Grid>

                    </Grid>
                </Grid>
            </Hidden>

            <Grid item container direction={"column"} xs>
                <Grid item container direction={"row"} style={{margin: '20px 0'}}>
                    <StyledLeftGrid item sm={12} md={6} container direction={"column"} spacing={1}>
                        <Grid item>
                            <StyledH5>{book.title}</StyledH5>
                        </Grid>

                        {book.title_english &&
                            <Grid item>
                                <StyledH6>{book.title_english}</StyledH6>
                            </Grid>
                        }


                        {book.page_amount &&
                            <Grid item container direction={"row"}>
                                <Grid item xs={true} container direction={"row"} alignItems={"center"}>
                                    <CustomFileIcon style={{fill: 'none'}}/>
                                    <span
                                        style={{fontSize: '0.9rem'}}>{Math.round(book.page_amount)} {t('foreignBook.pages')}</span>
                                </Grid>
                            </Grid>
                        }

                        <Grid item>
                            <StyledSpan1>{t('common:authors')}:</StyledSpan1>
                            <StyledSpan2>{book.authors}</StyledSpan2>
                        </Grid>

                        <Grid item>
                            <StyledSpan1>{t('common:isbn')}:</StyledSpan1>
                            <StyledSpan2>{book.isbn}</StyledSpan2>
                        </Grid>

                        <Grid item>
                            <StyledSpan1>{t('common:publisher')}:</StyledSpan1>
                            <StyledSpan2>
                                {book.publisher_url &&
                                    <a href={book.publisher_url} target="_blank"
                                       onClick={publisherDetailsClicked}>{book.publisher}</a>
                                }

                                {!book.publisher_url &&
                                    `${book.publisher}`
                                }
                            </StyledSpan2>

                        </Grid>

                        <Grid item>
                            <StyledSpan1>{t('foreignBook.mainCategory')}:</StyledSpan1>
                            <StyledSpan2>{t('common:mainCategories.' + BaseService.parseOldMainCategory(book.main_category))}</StyledSpan2>
                        </Grid>

                        {book.thema_category?.length > 0 &&
                            <Grid item>
                                <StyledSpan1>{t('common:categories')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
                                    {book.thema_category.map((category) => (
                                        <Grid item key={category.id} style={{maxWidth: '100%'}}>
                                            <StyledCategoryChip size="small" label={category.name}
                                                                style={{maxWidth: '100%'}}/>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        }

                        {book.tags?.length > 0 &&
                            <Grid item>
                                <StyledSpan1>{t('common:tags')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
                                    {book.tags.filter((tag) => tag.tag_type === 'tag').map((tag) => (
                                        <Grid item key={tag.id} style={{maxWidth: '100%'}}>
                                            <StyledTagChip size="small" label={tag.name} style={{maxWidth: '100%'}}/>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        }
                    </StyledLeftGrid>

                    <Grid item sm={12} md={6} style={{paddingLeft: '10px'}}>
                        <Tabs value={tabActive} onChange={(event, newValue) => {
                            setTabActive(newValue)
                        }}>
                            <Tab label={t('common:desc')} style={{fontSize: '0.9rem'}}/>
                            {(book.fragment && book.fragment !== "No fragment added") &&
                                <Tab label={t('common:fragment')} style={{fontSize: '0.9rem'}}/>
                            }
                        </Tabs>

                        <CustomTabPanel value={tabActive} index={0}>
                            <StyledDescription>{BaseService.cutText(book.description || '-', 500)}</StyledDescription>
                        </CustomTabPanel>
                        <CustomTabPanel value={tabActive} index={1}>
                            <StyledDescription>{BaseService.cutText(book.fragment || '-', 500)}</StyledDescription>
                        </CustomTabPanel>
                    </Grid>
                </Grid>

                <Grid item container justifyContent={"end"}>
                    <a href={book.book_url} target="_blank" style={{marginRight: '20px'}}>
                        <StyledButton type={"button"}
                                      onClick={bookDetailsClicked}>{t('foreignBook.bookDetails')}</StyledButton>
                    </a>
                </Grid>
            </Grid>

        </PublisherBookContainer>
    );
}

const PublisherBookContainer = styled(Grid)`
  border: 1px solid #E0E0E0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-bottom: 20px;
  padding: 10px 5px;
`;

const StyledImageContainer = styled(Grid)`
  margin-right: 10px !important;
  position: relative;
`;

const StyledImg = styled('img')`
  width: 218px;
  -webkit-box-shadow: -11px 0px 50px -34px ${Colors.neutral30};
  -moz-box-shadow: -11px 0px 50px -34px ${Colors.neutral30};
  box-shadow: -11px 0px 50px -34px ${Colors.neutral30};
`;

const StyledLeftGrid = styled(Grid)`
  border-right: 1px solid #E0E0E0;
  border-bottom: 0;

  @media (max-width: 959px) {
    border-right: 0;
    border-bottom: 1px solid #E0E0E0;
  }
`;

const StyledSpan1 = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.grayText};
`;

const StyledSpan2 = styled('p')`
  font-weight: 500;
  font-size: 0.9rem;

  a {
    color: ${Colors.red};
    font-weight: bold;
  }
`;

const StyledCategoryChip = styled(Chip)`
  background-color: transparent;
  color: ${Colors.lightViolet};
`;

const StyledTagChip = styled(Chip)`
  background-color: #F5F5F5;
  color: #424242;
  border: 1px solid #C2C2C2;
`;

const StyledDescription = styled('p')`
  font-size: 0.9rem;
`;

const StyledH5 = styled('h5')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
`;

const StyledH6 = styled('h6')`
  font-size: 1rem;
  color: ${Colors.grayText};
  font-weight: 600;
`;
